import { zodResolver } from '@hookform/resolvers/zod';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { VyAutocompleteSelect } from '../../../../components/new-design/inputs/VyAutocompleteSelect';
import { useApplyCredits } from '../../../../hooks/requests/use-apply-credits';
import { useCreditItems } from '../../../../hooks/requests/use-credit-items';
import { useCreditLedgerOverview } from '../../../../hooks/requests/use-credit-ledger-overview/use-credit-ledger-overview';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { runMutation } from '../../../../utils/vy-mutation';
import { ApplyCreditFormSchema, type ApplyCreditFormValues } from './apply-credit-form-schema';

type ApplyCreditDialogProps = {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceName: string;
  customerId: string;
  contractId?: string;
  maxAmount: number;
  currency: string;
};

export const ApplyCreditDialog: React.FC<ApplyCreditDialogProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceName,
  customerId,
  contractId,
  maxAmount,
  currency,
}) => {
  const applyCredits = useApplyCredits();
  const queryClient = useQueryClient();
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const { data: creditItems } = useCreditItems();
  const { data: creditLedgerData } = useCreditLedgerOverview(customerId);

  const availableBalance = creditLedgerData?.creditLedger?.balance;

  const productOptions =
    creditItems?.map(({ id, name }) => ({
      value: id,
      label: name,
    })) ?? [];

  const applyCreditSchema = useMemo(() => {
    const baseSchema = ApplyCreditFormSchema.refine((data) => data.amount <= maxAmount, {
      message: `Amount cannot exceed ${maxAmount}`,
      path: ['amount'],
    });

    if (availableBalance == null) {
      return baseSchema;
    }

    return baseSchema.refine((data) => data.amount <= availableBalance, {
      message: `Amount cannot exceed available balance (${availableBalance} ${currency})`,
      path: ['amount'],
    });
  }, [availableBalance, maxAmount, currency]);

  const form = useForm<ApplyCreditFormValues>({
    resolver: zodResolver(applyCreditSchema),
    defaultValues: {
      amount: undefined,
      invoiceId,
      customerId,
      contractId,
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    await runMutation(
      applyCredits,
      { invoiceId: formData.invoiceId, creditAmount: formData.amount },
      {
        pending: 'Applying credit...',
        success: 'Credit applied',
        error: 'Failed to apply credit',
      },
      {
        onSuccess: async () => {
          onClose();
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.creditLedgerOverview(customerId)),
            queryClient.invalidateQueries(queryKeys.invoicesOverviewsNew()),
            queryClient.invalidateQueries(queryKeys.creditNotes(customerId)),
            queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId)),
            queryClient.invalidateQueries(queryKeys.invoice(invoiceId)),
          ]);
        },
      },
    );
  });

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Apply credit"
      labelDescription={`Create a credit note for invoice ${invoiceName}`}
      ctaText="Apply credit"
      cancelButtonText="Cancel"
      onSubmit={handleSubmit}
      onClick={(event) => event.stopPropagation()}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px', width: '100%' }}>
        <TextField
          fullWidth
          label="Amount"
          type="number"
          variant="filled"
          error={isNotNullOrUndefined(form.formState.errors.amount)}
          helperText={
            form.formState.errors.amount?.message ??
            `Max: ${maxAmount} ${currency}${availableBalance != null ? ` | Available balance: ${availableBalance} ${currency}` : ''}`
          }
          {...form.register('amount', { valueAsNumber: true })}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{
                    color: "text.secondary"
                  }}>{currency}</Typography>
                </InputAdornment>
              ),
            },

            htmlInput: { min: 0, max: maxAmount, step: 0.01 }
          }} />

        <TextField fullWidth label="Invoice Ref Number" variant="filled" value={invoiceName} disabled />

        {isNetSuiteEnabled && (
          <Controller
            name="erpId"
            control={form.control}
            render={({ field }) => (
              <VyAutocompleteSelect
                options={productOptions}
                fullWidth
                onOptionSelect={(option) => {
                  const value = typeof option === 'string' ? option : option?.value;
                  field.onChange(value);
                }}
                getOptionLabel={(option) => option.label}
                label="Product (optional)"
              />
            )}
          />
        )}

        <TextField
          fullWidth
          label="Description (optional)"
          variant="filled"
          multiline
          rows={3}
          {...form.register('description')}
        />
      </Box>
    </VyDialog>
  );
};
